import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Database, Download, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { vehicleApi } from '../services/vehicleApi';
import { dealApi } from '../services/dealApi';
import { companyApi } from '../services/companyApi';
import { cashflowApi } from '../services/cashflowApi';
import { FIN_PERIODS, FIN_PERIOD_LABEL, FIN_SOURCE, Financial, Round, Shareholder, companyDetailApi } from '../services/companyDetailApi';
import { SHARE_KIND } from '../services/codes';
import { Participant, Vehicle, KIND_LABEL } from '../types/vehicle';
import { Deal, Valuation } from '../types/deal';
import { Company } from '../types/company';
import { Cashflow, KIND_META } from '../types/cashflow';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { nowLocal, todayLocal } from '../lib/date';

/**
 * 데이터 내보내기 — factsheet 소개서 12쪽 「Raw 데이터 다운로드」(13종) 대조 (2026-10-08).
 * 메뉴 이름을 「Raw 데이터」에서 「데이터 내보내기」로 바꿨다(2026-11-02 점검) — 하는 일이 엑셀 내려받기다. 경로 /data 는 그대로.
 *
 * 우리 데이터로 만들 수 있는 것만 둔다. TIPS 현황 · 4대보험 가입자 명부는 받는 자료가 없어 뺐다.
 * 엑셀(.xlsx)로 내려받는다 — 금액이 숫자로 들어가 바로 더할 수 있다.
 * 운용보고서는 시트 넷(개요 · 조합원 · 투자자산 · 기간 원장)을 한 파일로 — 기간 말 기준으로 원장에서 다시 센다.
 */
type Row = (string | number | null | undefined)[];
interface Sheet { name: string; rows: Row[] }
interface Dataset { group: string; name: string; fields: string; sheets: () => Sheet[]; period?: boolean }

function download(name: string, sheets: Sheet[]) {
  const wb = XLSX.utils.book_new();
  for (const s of sheets) {
    const ws = XLSX.utils.aoa_to_sheet(s.rows.map((r) => r.map((v) => (v === undefined ? null : v))));
    // 열 너비 — 가장 긴 값에 맞춘다(한글은 두 칸)
    ws['!cols'] = (s.rows[0] ?? []).map((_, i) => ({
      wch: Math.min(50, Math.max(6, ...s.rows.map((r) => String(r[i] ?? '').replace(/[가-힣]/g, 'xx').length + 1))),
    }));
    XLSX.utils.book_append_sheet(wb, ws, s.name.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31));
  }
  XLSX.writeFile(wb, `${name}_${nowLocal().replace(' ', '_').replace(':', '')}.xlsx`);
}

/** 반기 · 온기 — 2026 상반기 · 2026 하반기 · 2026 온기 */
interface Period { label: string; from: string; to: string }
function periods(firstYear: number): Period[] {
  const out: Period[] = [];
  const today = todayLocal();
  for (let y = Number(today.slice(0, 4)); y >= firstYear; y--) {
    for (const [label, from, to] of [['하반기', '07-01', '12-31'], ['상반기', '01-01', '06-30'], ['온기', '01-01', '12-31']] as const) {
      if (`${y}-${from}` <= today) out.push({ label: `${y} ${label}`, from: `${y}-${from}`, to: `${y}-${to}` });
    }
  }
  return out;
}

const sum = (xs: Cashflow[]) => xs.reduce((s, f) => s + f.amount, 0);

export const RawDataPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [parts, setParts] = useState<Participant[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [vals, setVals] = useState<Valuation[]>([]);
  const [fin, setFin] = useState<Financial[]>([]);
  const [holders, setHolders] = useState<Shareholder[]>([]);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [loading, setLoading] = useState(true);
  const [tourRun, setTourRun] = useState(false);
  const [periodIdx, setPeriodIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, p, d, c, f, va, x] = await Promise.all([
        vehicleApi.list(), vehicleApi.allParticipants(), dealApi.list(), companyApi.list(), cashflowApi.list(), dealApi.valuations(), companyDetailApi.all(),
      ]);
      setVehicles(v); setParts(p); setDeals(d); setCompanies(c); setFlows(f); setVals(va);
      setFin(x.fin); setHolders(x.holders); setRounds(x.rounds);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const vName = useCallback((id: number) => vehicles.find((v) => v.id === id)?.name ?? '', [vehicles]);
  const cName = useCallback((id: number) => companies.find((c) => c.id === id)?.name ?? '', [companies]);

  const pers = useMemo(() => periods(Math.min(Number(todayLocal().slice(0, 4)), ...flows.map((f) => Number(f.occurredOn.slice(0, 4))))), [flows]);
  // 기본은 마지막으로 끝난 기간 — 진행 중인 반기를 먼저 고르면 반쯤 빈 보고서가 나온다 (#33)
  useEffect(() => {
    const today = todayLocal();
    const i = pers.findIndex((p) => p.to < today);
    setPeriodIdx(i < 0 ? 0 : i);
  }, [pers]);
  const period = pers[periodIdx] ?? pers[0];

  /** 운용보고서 — 기간 말까지의 원장 · 그때의 최신 평가 */
  const report = useCallback((p: Period): Sheet[] => {
    const upto = flows.filter((f) => f.occurredOn <= p.to);
    const within = upto.filter((f) => f.occurredOn >= p.from);
    const latest = (dealId: number) => vals.filter((v) => v.dealId === dealId && v.valuedOn <= p.to).sort((a, b) => b.valuedOn.localeCompare(a.valuedOn))[0];
    const held = (d: Deal) => upto.some((f) => f.dealId === d.id && f.kind === 'investment');
    const residual = (d: Deal) => {
      const exited = d.stage === '회수' || d.stage === '상각';
      return exited && upto.some((f) => f.dealId === d.id && f.kind === 'exit') ? 0 : latest(d.id)?.ourValue ?? 0;
    };
    const of = (vid: number, kinds: string[], xs = upto) => sum(xs.filter((f) => f.vehicleId === vid && kinds.includes(f.kind)));
    return [
      { name: '개요', rows: [
        ['기간', '자금원', '자금 구분', '법적 형태', 'VICS 코드', '결성일', '투자기간 종료', '만기', '약정총액', '납입 누계', '기간 납입',
          '투자 누계', '회수 누계', '분배 누계', '기간 보수', '평가 잔액', '원장 잔액'],
        ...vehicles.filter((v) => v.startedOn <= p.to).map((v) => [
          p.label, v.name, KIND_LABEL[v.kind], v.legalForm ?? '확인 전', v.vicsCode ?? '', v.formedOn ?? v.startedOn, v.investEndOn ?? '', v.maturityOn ?? '',
          parts.filter((x) => x.vehicleId === v.id).reduce((s, x) => s + (x.committedAmount ?? 0), 0),
          of(v.id, ['contribution']), of(v.id, ['contribution'], within),
          of(v.id, ['investment']), of(v.id, ['exit', 'dividend']), of(v.id, ['distribution']), of(v.id, ['fee'], within),
          deals.filter((d) => d.vehicleId === v.id && held(d)).reduce((s, d) => s + residual(d), 0),
          sum(upto.filter((f) => f.vehicleId === v.id && f.direction === 'in')) - sum(upto.filter((f) => f.vehicleId === v.id && f.direction === 'out')),
        ]),
      ] },
      { name: '조합원', rows: [
        ['자금원', '참여자', '운용', '약정', '납입 누계', '기간 납입', '분배 누계', '기간 분배', '지분'],
        ...parts.filter((x) => vehicles.some((v) => v.id === x.vehicleId && v.startedOn <= p.to)).map((x) => {
          const paid = sum(upto.filter((f) => f.participantId === x.id && f.kind === 'contribution'));
          const total = sum(upto.filter((f) => f.vehicleId === x.vehicleId && f.kind === 'contribution'));
          return [vName(x.vehicleId), x.name, x.isManager ? 'GP' : '', x.committedAmount, paid,
            sum(within.filter((f) => f.participantId === x.id && f.kind === 'contribution')),
            sum(upto.filter((f) => f.participantId === x.id && f.kind === 'distribution')),
            sum(within.filter((f) => f.participantId === x.id && f.kind === 'distribution')),
            x.shareRatio ?? (total ? Math.round((paid / total) * 10000) / 10000 : null)];
        }),
      ] },
      { name: '투자자산', rows: [
        ['자금원', '투자처', '증권', '투자일', '투자원금', '회수', '평가액', '평가일', 'MOIC'],
        ...deals.filter(held).map((d) => {
          const inv = sum(upto.filter((f) => f.dealId === d.id && f.kind === 'investment'));
          const ret = sum(upto.filter((f) => f.dealId === d.id && (f.kind === 'exit' || f.kind === 'dividend')));
          const res = residual(d);
          return [vName(d.vehicleId), cName(d.companyId), d.securityKind, d.investedOn, inv, ret, res, latest(d.id)?.valuedOn ?? '',
            inv ? Math.round(((ret + res) / inv) * 100) / 100 : null];
        }),
      ] },
      { name: '기간 원장', rows: [
        ['날짜', '자금원', '종류', '방향', '금액', '투자처', '참여자', '메모'],
        ...[...within].sort((a, b) => a.occurredOn.localeCompare(b.occurredOn) || a.id - b.id).map((f) => [f.occurredOn, vName(f.vehicleId), KIND_META[f.kind].label,
          f.direction === 'in' ? '들어옴' : '나감', f.amount, f.dealId ? cName(deals.find((d) => d.id === f.dealId)?.companyId ?? -1) : '',
          parts.find((x) => x.id === f.participantId)?.name ?? '', f.memo]),
      ] },
    ];
  }, [flows, vals, vehicles, parts, deals, vName, cName]);

  const one = (name: string, rows: Row[]): Sheet[] => [{ name, rows }];
  const sets: Dataset[] = useMemo(() => [
    { group: '투자 성과 · 이력', name: '투자 실적 요약', fields: '자금원 · 투자처 · 투자원금 · 회수 · 최신 평가 · 단계',
      sheets: () => one('투자 실적', [['자금원', '투자처', '증권', '투자일', '투자원금', '회수', '최신 평가', '단계'], ...deals.map((d) => {
        const fs = flows.filter((f) => f.dealId === d.id);
        const latest = vals.filter((x) => x.dealId === d.id).sort((a, b) => b.valuedOn.localeCompare(a.valuedOn))[0];
        return [vName(d.vehicleId), cName(d.companyId), d.securityKind, d.investedOn,
          sum(fs.filter((f) => f.kind === 'investment')), sum(fs.filter((f) => f.kind === 'exit' || f.kind === 'dividend')),
          latest?.ourValue ?? null, d.stage];
      })]) },
    { group: '투자 성과 · 이력', name: '투자기업 후속투자 유치이력', fields: '라운드 · 날짜 · Pre · Post · 금액 · 우리 참여',
      sheets: () => one('후속투자', [['투자처', '라운드', '날짜', 'Pre', 'Post', '라운드 금액', '우리 참여'],
        ...[...rounds].sort((a, b) => cName(a.companyId).localeCompare(cName(b.companyId)) || a.closedOn.localeCompare(b.closedOn))
          .map((r) => [cName(r.companyId), r.name, r.closedOn, r.preMoney, r.postMoney, r.amount, r.dealId ? 'Y' : ''])]) },
    { group: '투자 성과 · 이력', name: '원장 전체', fields: '날짜 · 자금원 · 종류 · 방향 · 금액 · 상대',
      sheets: () => one('원장', [['날짜', '자금원', '종류', '방향', '금액', '투자처', '참여자', '메모'], ...flows.map((f) => [f.occurredOn, vName(f.vehicleId), KIND_META[f.kind].label, f.direction === 'in' ? '들어옴' : '나감', f.amount,
        f.dealId ? cName(deals.find((d) => d.id === f.dealId)?.companyId ?? -1) : '', parts.find((p) => p.id === f.participantId)?.name ?? '', f.memo])]) },
    { group: '투자 성과 · 이력', name: '반기 · 온기 운용보고서', fields: '개요 · 조합원 · 투자자산 · 기간 원장 — 시트 넷', period: true,
      sheets: () => (period ? report(period) : []) },
    { group: '투자기업 정보', name: '투자기업 기본정보', fields: '대표 · 사업자번호 · 법인번호 · 설립일 · 주소 · 업종코드 · 결산월',
      sheets: () => one('기본정보', [['투자처', '대표', '사업자번호', '법인번호', '설립일', '본사 주소', '업종코드', '결산월', '임직원 수', '제품 · 서비스'], ...companies.map((c) => (
        [c.name, c.ceo, c.bizNo, c.corpNo, c.foundedOn, c.address, c.industryCode, c.fiscalMonth, c.employees, c.products]))]) },
    { group: '투자기업 정보', name: '투자기업 벤처기업 인증현황', fields: '투자처 · 벤처인증',
      sheets: () => one('벤처인증', [['투자처', '벤처인증'], ...companies.map((c) => [c.name, c.ventureCert == null ? '모름' : c.ventureCert ? '있음' : '없음'])]) },
    { group: '투자기업 정보', name: '투자기업 담당자 정보', fields: '담당자 · 직함 · 이메일 · 전화',
      sheets: () => one('담당자', [['투자처', '담당자', '직함', '이메일', '전화'], ...companies.map((c) => [c.name, c.contactName, c.contactTitle, c.contactEmail, c.contactPhone])]) },
    { group: '재무 · 주식', name: '투자기업 주주정보', fields: '기준일 · 주주 · 종류 · 주식수 · 지분율',
      sheets: () => one('주주', [['투자처', '기준일', '주주', '종류', '주식수', '지분율'],
        ...[...holders].sort((a, b) => cName(a.companyId).localeCompare(cName(b.companyId)) || b.asOf.localeCompare(a.asOf)).map((s) => {
          const tot = holders.filter((x) => x.companyId === s.companyId && x.asOf === s.asOf).reduce((a, x) => a + Number(x.shares), 0);
          return [cName(s.companyId), s.asOf, s.name, SHARE_KIND.toUi(s.shareKind), Number(s.shares), tot ? Math.round((Number(s.shares) / tot) * 10000) / 10000 : null];
        })]) },
    { group: '재무 · 주식', name: '투자기업 재무상태표', fields: '연도 · 기간 · 현금 · 자산 · 부채 · 자본',
      sheets: () => one('재무상태표', [['투자처', '연도', '기간', '구분', '현금성자산', '투자자산', '자산총계', '유동부채', '비유동부채', '부채총계', '자본금', '자본총계', '출처'],
        ...[...fin].sort((a, b) => cName(a.companyId).localeCompare(cName(b.companyId)) || a.fiscalYear - b.fiscalYear || FIN_PERIODS.indexOf(a.fiscalPeriod ?? 'FY') - FIN_PERIODS.indexOf(b.fiscalPeriod ?? 'FY')).map((f) => [cName(f.companyId), f.fiscalYear, FIN_PERIOD_LABEL[f.fiscalPeriod ?? 'FY'], f.isForecast ? '전망' : '실적',
          f.cashEquivalents, f.investmentAssets, f.totalAssets, f.currentLiabilities, f.noncurrentLiabilities, f.totalLiabilities, f.capitalStock, f.totalEquity, FIN_SOURCE.toUi(f.source)])]) },
    { group: '재무 · 주식', name: '투자기업 손익계산서', fields: '연도 · 기간 · 매출 · 영업이익 · 순이익',
      sheets: () => one('손익계산서', [['투자처', '연도', '기간', '구분', '매출액', '매출원가', '매출총이익', '판매관리비', '영업이익', '이자비용', '당기순이익'],
        ...[...fin].sort((a, b) => cName(a.companyId).localeCompare(cName(b.companyId)) || a.fiscalYear - b.fiscalYear || FIN_PERIODS.indexOf(a.fiscalPeriod ?? 'FY') - FIN_PERIODS.indexOf(b.fiscalPeriod ?? 'FY')).map((f) => [cName(f.companyId), f.fiscalYear, FIN_PERIOD_LABEL[f.fiscalPeriod ?? 'FY'], f.isForecast ? '전망' : '실적',
          f.revenue, f.costOfSales, f.grossProfit, f.sgaExpenses, f.operatingIncome, f.interestExpense, f.netIncome])]) },
    { group: '자금원 · 조합원', name: '자금원(조합)별 기본정보', fields: '이름 · 자금 구분 · 법적 형태 · 사업자번호 · 통장 명의 · 시작일',
      sheets: () => one('자금원', [['자금원', '자금 구분', '법적 형태', '사업자번호', '통장 명의', '시작일', '상태'], ...vehicles.map((v) => [v.name, KIND_LABEL[v.kind], v.legalForm ?? '확인 전', v.bizNo, v.accountName, v.startedOn, v.status])]) },
    { group: '자금원 · 조합원', name: '조합원 정보', fields: '자금원 · 참여자 · 운용 · 약정 · 납입 · 분배',
      sheets: () => one('조합원', [['자금원', '참여자', '운용', '약정', '납입', '분배'], ...parts.map((p) => [vName(p.vehicleId), p.name, p.isManager ? 'GP' : '', p.committedAmount,
        sum(flows.filter((f) => f.participantId === p.id && f.kind === 'contribution')), sum(flows.filter((f) => f.participantId === p.id && f.kind === 'distribution'))])]) },
  ], [deals, flows, vals, companies, vehicles, parts, fin, holders, rounds, vName, cName, period, report]);

  const groups = Array.from(new Set(sets.map((s) => s.group)));

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'sets', target: '[data-tour="sets"]', title: '데이터셋', content: '지금 데이터로 바로 만듭니다. 누르면 엑셀(.xlsx)로 받습니다. 금액은 숫자 그대로라 엑셀에서 바로 더할 수 있습니다.' },
    { name: 'period', target: '[data-tour="period"]', title: '운용보고서 기간', content: '반기 · 온기를 고르면 그 기간 말까지의 원장과 그때의 최신 평가로 다시 셉니다. 개요 · 조합원 · 투자자산 · 기간 원장 네 시트가 한 파일에 들어갑니다.' },
  ], []);

  if (loading) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>;

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">데이터 내보내기 <TourButton onClick={() => setTourRun(true)} /></h1>
        <span className="rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-600">데이터셋 {sets.length}</span>
      </div>
      <div data-tour="sets" className="space-y-5">
        {groups.map((g) => (
          <section key={g}>
            <h2 className="text-xs font-semibold text-slate-500 mb-2">{g}</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {sets.filter((s) => s.group === g).map((s) => {
                const sh = s.sheets();
                const n = sh.reduce((a, x) => a + x.rows.length - 1, 0);
                return (
                  <div key={s.name} className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col gap-2">
                    <div className="flex items-center gap-2 font-medium text-slate-900"><Database size={14} className="text-indigo-500" />{s.name}</div>
                    <p className="text-xs text-slate-500 flex-1">{s.fields}</p>
                    {s.period && (
                      <select data-tour="period" aria-label="기간" value={periodIdx} onChange={(e) => setPeriodIdx(Number(e.target.value))}
                        className="rounded border border-slate-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200">
                        {pers.map((p, i) => <option key={p.label} value={i}>{p.label} · {p.from.slice(5).replace('-', '.')}–{p.to.slice(5).replace('-', '.')}</option>)}
                      </select>
                    )}
                    <button onClick={() => download(s.period && period ? `운용보고서_${period.label.replace(' ', '_')}` : s.name, sh)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                      <Download size={12} /> 엑셀 내려받기 · {n}행{sh.length > 1 ? ` · 시트 ${sh.length}` : ''}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};
